import type {
  AccordionElement,
  DashboardElement,
  DashboardSpec,
  TabsElement,
} from "../dashboard/catalog"

import { resolveTileByPath } from "./tile-helpers"

type LocatedElement = {
  element: DashboardElement
  path: string
}

function hasStableIdentityMatch(previous: DashboardElement, candidate: DashboardElement): boolean {
  const previousRecord = previous as Record<string, unknown>
  const candidateRecord = candidate as Record<string, unknown>
  for (const key of ["id", "title", "label", "stateKey"]) {
    const previousValue = previousRecord[key]
    const candidateValue = candidateRecord[key]
    if (
      typeof previousValue === "string" &&
      previousValue.trim() &&
      previousValue === candidateValue
    ) {
      return true
    }
  }

  // Manual title/label edits clone a tile at the same path. Preserve the
  // binding only when every non-presentation field is still identical;
  // type-at-path alone is too weak and can attach old SQL to a new tile.
  if (previous.type !== candidate.type) return false
  const withoutPresentation = (element: DashboardElement) => {
    const { title: _title, label: _label, ...rest } = element as Record<string, unknown>
    return rest
  }
  return (
    JSON.stringify(withoutPresentation(previous)) === JSON.stringify(withoutPresentation(candidate))
  )
}

function collectLeafElements(children: DashboardElement[], prefix = ""): LocatedElement[] {
  const result: LocatedElement[] = []

  children.forEach((element, index) => {
    const path = prefix + "children[" + index + "]"
    if (element.type === "Grid" || element.type === "Row") {
      result.push(
        ...collectLeafElements((element as { children: DashboardElement[] }).children, path + ".")
      )
      return
    }
    if (element.type === "Tabs") {
      ;(element as TabsElement).tabs.forEach((tab, tabIndex) => {
        result.push(...collectLeafElements(tab.children, path + ".tabs[" + tabIndex + "]."))
      })
      return
    }
    if (element.type === "Accordion") {
      ;(element as AccordionElement).items.forEach((item, itemIndex) => {
        result.push(...collectLeafElements(item.children, path + ".items[" + itemIndex + "]."))
      })
      return
    }
    result.push({ element, path })
  })

  return result
}

/**
 * Keep persisted query bindings aligned when the visual editor removes or
 * reorders tiles. UI mutations preserve references for unchanged tiles, which
 * lets us rebase their element paths without guessing from array positions.
 * A cloned tile at the same path (for example a title edit) keeps its binding.
 */
export function reconcileDashboardQueryBindings(
  previous: DashboardSpec,
  next: DashboardSpec
): DashboardSpec {
  if (!previous.queryBindings?.length) return next

  const previousLocations = collectLeafElements(previous.children)
  const nextLocations = collectLeafElements(next.children)
  const previousPathByReference = new Map(
    previousLocations.map(({ element, path }) => [element, path])
  )
  const nextPathByReference = new Map(nextLocations.map(({ element, path }) => [element, path]))

  const queryBindings = previous.queryBindings.flatMap((binding) => {
    const previousElement = resolveTileByPath(previous, binding.element_path)
    if (!previousElement) return []

    let nextPath = nextPathByReference.get(previousElement)
    if (!nextPath) {
      const candidate = resolveTileByPath(next, binding.element_path)
      const candidateWasAnotherExistingElement =
        candidate !== undefined &&
        previousPathByReference.has(candidate) &&
        candidate !== previousElement

      if (
        candidate &&
        hasStableIdentityMatch(previousElement, candidate) &&
        !candidateWasAnotherExistingElement
      ) {
        nextPath = binding.element_path
      }
    }

    return nextPath ? [{ ...binding, element_path: nextPath }] : []
  })

  return { ...next, queryBindings }
}
